/* eslint-disable react/require-default-props */
/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-explicit-any */
import IconMinus from '@assets/icons/minus.svg?react';
import IconPlus from '@assets/icons/plus.svg?react';
import IconSave from '@assets/icons/save.svg?react';
import {
 Box,
 FormControl,
 FormHelperText,
 IconButton,
 InputLabel,
 MenuItem,
 Popover,
 Select,
 TextField,
 Theme,
 useTheme,
} from '@mui/material';
import { SetStateAction, useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { usePage } from '../../../contexts/usePageContext';
import {
 getConfigStorageItem,
 setConfigStorageItem,
} from '../../../hooks/useConfigStorage';
import { changeCurrentPreset } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { RootState } from '../../../redux/store';

interface LimitFormValues {
 limit: string;
 value: number;
 color: string;
}

const LimitModal = ({
 data,
 setSelected = () => {},
 setOpen,
}: {
 data: any;
 setSelected?: SetStateAction<any>;
 setOpen: SetStateAction<any>;
}) => {
 const th = useTheme();
 const { pageName } = usePage();
 const dispatch = useDispatch();
 const { t } = useTranslation();

 const currentColumnsPreset = useSelector(
  (state: RootState) =>
   state.clientConfiguration.currentColumnsPreset?.[pageName] || null,
 );

 const { colName, anchorElement } = data;
 const tempState = getConfigStorageItem(pageName).columns;
 const limits = tempState?.limit;
 const thisLimits = limits?.[colName] || [];

 const {
  handleSubmit,
  control,
  formState: { errors },
  reset,
  watch,
 } = useForm<{ rows: LimitFormValues[] }>({
  mode: 'onChange',
  defaultValues: { rows: [{ limit: '<', value: 0, color: 'red' }] },
 });

 const { fields, append, remove } = useFieldArray({
  control,
  name: 'rows',
 });

 const handleSave = (formData: any) => {
  let newLimits = {};
  if (limits && !Object.keys(limits).length) {
   newLimits = {
    [colName]: formData.rows,
   };
  } else if (formData.rows.length === 0) {
   newLimits = Object.keys(limits)
    .filter((key) => key !== colName)
    .reduce((acc, key) => {
     (acc as any)[key] = limits[key];
     return acc;
    }, {});
  } else {
   newLimits = {
    ...limits,
    [colName]: formData.rows,
   };
  }

  if (currentColumnsPreset) {
   dispatch(
    changeCurrentPreset({
     page: pageName,
     type: 'columns',
     preset: { ...currentColumnsPreset, limit: newLimits },
     temp: true,
    }),
   );
   dispatch(
    changeCurrentPreset({
     page: pageName,
     type: 'columns',
     preset: { ...currentColumnsPreset, limit: newLimits },
     temp: false,
    }),
   );

   // move this somehow
   setConfigStorageItem({
    page: pageName,
    key: 'columns',
    value: { ...currentColumnsPreset, limit: newLimits },
   });
  }

  setOpen(false);
 };

 useEffect(() => {
  if (thisLimits.length) {
   reset({ rows: thisLimits });
  }
 }, []);

 const colors = [
  {
   name: 'red',
   fill: th.palette.kemek.lightRed,
   stroke: th.palette.kemek.red,
  },
  {
   name: 'green',
   fill: th.palette.kemek.lightGreen,
   stroke: th.palette.kemek.green,
  },
  {
   name: 'orange',
   fill: th.palette.kemek.lightOrange,
   stroke: th.palette.kemek.orange,
  },
  {
   name: 'yellow',
   fill: th.palette.kemek.lightYellow,
   stroke: th.palette.kemek.yellow,
  },
 ];

 return (
  <Popover
   id="limit-modal"
   open={!!anchorElement}
   anchorEl={anchorElement}
   onClose={() => setOpen(false)}
   elevation={0}
   anchorOrigin={{
    vertical: 'bottom',
    horizontal: 'left',
   }}
  >
   <Box
    component="form"
    onSubmit={handleSubmit(handleSave)}
    noValidate
    sx={{
     minWidth: '300px',
     borderRadius: '4px',
     border: `1px solid ${th.palette.kemek.lightestGray}`,
     backgroundColor: (theme: Theme) => theme.palette.kemek.lightBlue,
     p: '4px',
     pt: '12px',
     display: 'flex',
     flexDirection: 'column',
     gap: 1,
    }}
   >
    {fields.map((f, index) => (
     <Box
      key={f.id}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}
     >
      {/* //alignItems: 'center', */}
      {/* limit */}
      <Controller
       name={`rows.${index}.limit` as const}
       control={control}
       rules={{
        required: `${t('limitModal.limit')} ${t('global.isRequired')}`,
       }}
       render={({ field }) => (
        <FormControl
         variant="outlined"
         //  sx={{ height: 'auto' }}
         error={!!errors?.rows?.[index]?.limit}
        >
         <InputLabel
          id={`limit-label-${index}`}
          shrink={field.value === '' ? true : undefined}
         >
          {t('limitModal.limit')}
         </InputLabel>
         <Select
          disableUnderline
          {...field}
          id={`limit-value-${index}`}
          labelId={`limit-label-${index}`}
          size="small"
          label={t('limitModal.limit')}
          sx={{
           background: (theme: Theme) => theme.palette.kemek.white,
           width: 120,
          }}
          displayEmpty
          renderValue={(value) => {
           if (value === '') {
            return '\u00A0';
           }
           if (value === '>') {
            return t('limitModal.more');
           }
           if (value === '<') {
            return t('limitModal.less');
           }
           return value;
          }}
          //   inputProps={{ 'aria-label': 'Without label' }}
         >
          <MenuItem value="" />
          <MenuItem value=">">{t('limitModal.more')}</MenuItem>
          <MenuItem value="<">{t('limitModal.less')}</MenuItem>
         </Select>
         {(errors as any)?.rows?.[index]?.limit && (
          <FormHelperText
           sx={{
            whiteSpace: 'nowrap',
            fontSize: 9,
            m: 0,
            // position: 'absolute',
            // bottom: -16,
            // left: 0,
           }}
          >
           {(errors as any)?.rows?.[index]?.limit.message}
          </FormHelperText>
         )}
        </FormControl>
       )}
      />
      {/* value */}
      <Controller
       name={`rows.${index}.value` as const}
       control={control}
       defaultValue={0}
       rules={{
        required: `${t('limitModal.value')} ${t('global.isRequired')}`,
       }}
       render={({ field }) => (
        <FormControl sx={{}} error={!!errors?.rows?.[index]?.value}>
         <TextField
          // size="small"
          // slotProps={{
          //  input: {
          //   autoComplete: 'off',
          //   disableUnderline: true,
          //  },
          // }}
          {...field}
          error={!!(errors as any).rows?.[index]?.value}
          //   helperText={(errors as any).rows?.[index]?.value?.message}
          variant="outlined"
          size="small"
          id="outlined-basic"
          label={t('limitModal.value')}
          sx={{
           '& .MuiOutlinedInput-root': {
            background: (theme: Theme) => theme.palette.kemek.white,
           },
           //  '& .MuiInputBase-input': {
           //   height: '13px',
           //  },
           maxWidth: 120,
          }}
         />
         {(errors as any)?.rows?.[index]?.value && (
          <FormHelperText
           sx={{
            whiteSpace: 'nowrap',
            fontSize: 9,
            m: 0,
            // position: 'absolute',
            // bottom: -16,
            // left: 0,
           }}
          >
           {(errors as any)?.rows?.[index]?.value.message}
          </FormHelperText>
         )}
        </FormControl>
       )}
      />
      {/* color */}
      <Controller
       name={`rows.${index}.color` as const}
       control={control}
       rules={{
        required: `${t('limitModal.color')} ${t('global.isRequired')}`,
       }}
       render={({ field }) => (
        <FormControl
         variant="outlined"
         sx={{}}
         error={!!errors?.rows?.[index]?.color}
        >
         <InputLabel
          id={`select-color-label-${index}`}
          shrink={field.value === '' ? true : undefined}
         >
          {t('limitModal.color')}
         </InputLabel>
         <Select
          disableUnderline
          {...field}
          id={`select-color-${index}`}
          labelId={`select-color-label-${index}`}
          size="small"
          label={t('limitModal.color')}
          sx={{
           background: (theme: Theme) => theme.palette.kemek.white,
           minWidth: 100,
           '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
           },
          }}
          displayEmpty
          renderValue={(value) => {
           if (value === '') {
            return '\u00A0';
           }
           const selectedColor = colors.find((color) => color.name === value);
           if (!selectedColor) {
            return '\u00A0';
           }
           return (
            <svg
             xmlns="http://www.w3.org/2000/svg"
             width="16"
             height="16"
             viewBox="0 0 16 16"
             fill="none"
            >
             <circle
              cx="8"
              cy="8"
              r="6.5"
              fill={selectedColor.fill}
              stroke={selectedColor.stroke}
              strokeWidth="3"
             />
            </svg>
           );
          }}
         >
          <MenuItem value="" />
          {colors.map((color) => (
           <MenuItem value={color.name} key={color.name}>
            <svg
             xmlns="http://www.w3.org/2000/svg"
             width="16"
             height="16"
             viewBox="0 0 16 16"
             fill="none"
            >
             <circle
              cx="8"
              cy="8"
              r="6.5"
              fill={color.fill}
              stroke={color.stroke}
              strokeWidth="3"
             />
            </svg>
           </MenuItem>
          ))}
         </Select>
         {(errors as any)?.rows?.[index]?.color && (
          <FormHelperText
           sx={{
            whiteSpace: 'nowrap',
            fontSize: 9,
            m: 0,
            // position: 'absolute',
            // bottom: -16,
            // left: 0,
           }}
          >
           {(errors as any)?.rows?.[index]?.color.message}
          </FormHelperText>
         )}
        </FormControl>
       )}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
       <IconButton
        type="button"
        size="large"
        edge="start"
        color="inherit"
        aria-label="remove row"
        sx={{ ml: 0 }}
        onClick={() => {
         remove(index);
         if (index === 0 && fields.length === 1) {
          handleSubmit(handleSave)();
          setOpen(false);
         }
        }}
       >
        <IconMinus />
       </IconButton>
      </Box>
     </Box>
    ))}
    <Box
     sx={{
      display: 'flex',
      justifyContent: 'space-between',
     }}
    >
     <IconButton
      type="button"
      sx={{ ml: 0 }}
      onClick={() => append({ limit: '', value: 0, color: 'red' })}
     >
      <IconPlus style={{ color: th.palette.kemek.darkGray }} />
     </IconButton>
     <IconButton type="submit" sx={{ ml: 'auto', mr: '12px' }}>
      <IconSave style={{ color: th.palette.kemek.blue }} />
     </IconButton>
     <IconButton
      type="button"
      sx={{ fontSize: 12, color: th.palette.kemek.darkGray }}
      onClick={() => {
       remove();
       handleSubmit(handleSave)();
       setOpen(false);
      }}
     >
      <IconMinus />
     </IconButton>
    </Box>
   </Box>
  </Popover>
 );
};

export default LimitModal;

